import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Ragna, the Horn (models/ragna.ts, docs/ANTIHEROES.md): built on the fallen sentinel's bones, so she walks, reels,
 * staggers and falls as the sentinel does (a touch quicker: the sim walks her faster). Her own: the axe's chop
 * (attack, landing at 0.13 s), the leap (a crouch, then up and over, landing with the axe down at 0.62 s: her flight
 * is 8 ticks), the horn (raised to her mouth, her head thrown back), the channel of the lone sally (her cry: arms
 * wide, shaking) and the sally itself (four seconds turning on her heel, the axe out).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on the
 * trunk leans it forward, +Y turns the trunk to its left.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** The axe up over the right shoulder, the body coiled: the chop's wind-up. */
const RAISED: P = {
  root: [0, 0.15, 0, 0, -0.02, -0.04],
  body: [-0.05, -0.25, 0],
  chest: [-0.15, -0.4, 0.05],
  head: [0.02, 0.35, 0],
  armR: [-2.6, 0, -0.3],
  foreR: [-0.8, 0, 0],
  sword: [-0.4, 0, 0],
  armL: [-0.3, 0, 0.3],
  foreL: [-0.6, 0, 0],
  legL: [-0.25, 0, -0.05],
  shinL: [0.25, 0, 0],
  legR: [0.3, 0, 0.05],
  shinR: [0.35, 0, 0],
};
/** The chop lands: the axe down and across, her weight over the front foot. */
const CHOPPED: P = {
  root: [0, -0.2, 0, 0, -0.07, 0.15],
  body: [0.2, 0.3, 0],
  chest: [0.3, 0.5, -0.05],
  head: [0.1, -0.4, 0],
  armR: [-0.4, 0, 0.3],
  foreR: [-0.15, 0, 0],
  sword: [0.7, 0, 0],
  armL: [0.3, 0, 0.3],
  legL: [-0.6, 0, -0.05],
  shinL: [0.5, 0, 0],
  legR: [0.45, 0, 0.05],
  shinR: [0.15, 0, 0],
  flapF: [-0.4, 0, 0],
  flapB: [0.3, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 0.9, false, (b, t) => {
    timeline(b, t, [
      [0, RAISED],
      [0.05, { ...RAISED, armR: [-2.75, 0, -0.35] }, outQuad],
      [0.13, CHOPPED, inCubic],
      [0.35, CHOPPED, outCubic],
      [0.9, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0, 0, -0.02 * kick(t, 0.13, 6, 9));
  }, (t) => thud(t, [[0.13, 0.04]]), 'Hachazo');

/** The leap: a crouch, up and over with the axe raised, down on her prey with the blade into the ground. */
const leap = (): Clip<B> =>
  clip<B>('leap', 1.3, false, (b, t) => {
    const crouch: P = { root: [0.3, 0, 0, 0, -0.18, 0], body: [0.3, 0, 0], chest: [0.2, 0, 0], legL: [-0.9, 0, 0], shinL: [1.3, 0, 0], legR: [-0.6, 0, 0], shinR: [1.1, 0, 0], armR: [0.4, 0, 0.2], armL: [0.4, 0, 0.2] };
    const air: P = { root: [-0.15, 0, 0, 0, 0, 0], body: [-0.1, 0, 0], chest: [-0.2, 0, 0], head: [-0.2, 0, 0], armR: [-2.9, 0, -0.2], foreR: [-0.4, 0, 0], sword: [-0.4, 0, 0], armL: [-1.2, 0, 0.6], legL: [-1.0, 0, 0], shinL: [1.4, 0, 0], legR: [0.2, 0, 0], shinR: [0.8, 0, 0], flapF: [0.4, 0, 0], flapB: [-0.5, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.08, crouch, outQuad],
      [0.22, air, outExpo],
      [0.55, air],
      [0.62, { ...CHOPPED, root: [0.2, 0, 0, 0, -0.2, 0.1], legL: [-0.9, 0, 0], shinL: [1.1, 0, 0] }, inCubic],
      [0.9, CHOPPED],
      [1.3, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    // Up and down along the flight (the sim moves her across the ground).
    const k = t > 0.12 && t < 0.62 ? Math.sin(((t - 0.12) / 0.5) * Math.PI) : 0;
    b.root.pos(0, 0.9 * k, 0);
  }, (t) => thud(t, [[0.62, 0.1]]), 'Salto');

/** The horn: raised to her mouth, her head thrown back, and blown. */
const horn = (): Clip<B> =>
  clip<B>('horn', 1.6, false, (b, t) => {
    const blow: P = { body: [-0.1, 0.15, 0], chest: [-0.25, 0.25, 0], head: [-0.45, 0.1, 0], armL: [-2.2, 0, 0.55], foreL: [-1.5, 0, 0], shield: [-0.4, 0, 0], armR: [0.35, 0, 0.35], legL: [-0.2, 0, -0.08], legR: [0.2, 0, 0.08] };
    timeline(b, t, [
      [0, {}],
      [0.25, blow, outExpo],
      [1.2, blow],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    const sh = t > 0.25 && t < 1.2 ? Math.sin(t * 60) * 0.015 : 0;
    b.chest.rot(-0.25 + sh, 0.25, 0);
  }, undefined, 'Cuerno');

/** The lone sally, channelled: her cry — arms thrown wide, the axe out, shaking with it. */
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.035;
    b.root.pos(0, -0.06 + 0.01 * Math.sin(p * 2), 0).rot(-0.05, 0, 0);
    b.body.rot(-0.12, 0, 0);
    b.chest.rot(-0.3 + shake, 0, 0);
    b.head.rot(-0.5, 0, 0);
    b.armR.rot(-1.5 + shake, 0, -1.1);
    b.foreR.rot(-0.1);
    b.sword.rot(0.2 + shake * 2);
    b.armL.rot(-1.5 - shake, 0, 1.1);
    b.foreL.rot(-0.1);
    b.legL.rot(-0.3, 0, -0.12);
    b.shinL.rot(0.35);
    b.legR.rot(0.3, 0, 0.12);
    b.shinR.rot(0.4);
    b.flapF.rot(0.12 * Math.sin(p * 3));
    b.flapB.rot(-0.15 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.007;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The sally: four seconds turning on her heel, the axe held out at arm's length. */
const sally = (): Clip<B> =>
  clip<B>('sally', 4.0, false, (b, t) => {
    const out = t < 0.15 ? t / 0.15 : t > 3.7 ? (4 - t) / 0.3 : 1;
    b.root.pos(0, -0.05 * out, 0).rot(0, -t * TAU * 2.2, 0);
    b.body.rot(0.1 * out, 0, 0);
    b.chest.rot(0.05 * out, -0.3 * out, 0);
    b.head.rot(0, 0.3 * out, 0);
    b.armR.rot(-1.45 * out, 0, -0.5 * out);
    b.foreR.rot(-0.05 * out);
    b.sword.rot(0.9 * out);
    b.armL.rot(-1.0 * out, 0, 0.9 * out);
    b.legL.rot(-0.25 * out, 0, -0.1 * out);
    b.shinL.rot(0.35 * out);
    b.legR.rot(0.25 * out, 0, 0.1 * out);
    b.shinR.rot(0.3 * out);
    b.flapF.rot(-0.6 * out);
    b.flapB.rot(0.6 * out);
  }, undefined, 'La salida sola');

/**
 * Kneeling to the clan's call (El Cuerno 4): she stops dead and her head comes up at the horn (0.3 s), then she sinks
 * to her knees as the sentinel she is built on falls, the axe planted head-down at her side (1.2 s), and bows her head.
 * Holds there (not looping: the fight ends while she kneels).
 */
const kneel = (): Clip<B> =>
  clip<B>('kneel', 2.2, false, (b, t) => {
    const hear: P = { body: [-0.08, 0.1, 0], chest: [-0.12, 0.1, 0], head: [-0.35, 0.3, 0], armR: [0.25, 0, 0.25], sword: [0.6, 0, 0], armL: [0.2, 0, 0.25] };
    // Down on both knees as the fallen sentinel sinks (sentinel.ts), the axe planted head-down at her side, the horn hand on her knee.
    const down: P = {
      root: [0, 0, 0, 0, -0.5, -0.1],
      body: [0.18, 0, 0.04],
      chest: [0.15, 0, 0],
      head: [0.3, 0, 0.1],
      legL: [0.05, 0, -0.08],
      shinL: [1.55, 0, 0],
      legR: [0.1, 0, 0.08],
      shinR: [1.55, 0, 0],
      armR: [0.15, 0, 0.15],
      foreR: [-0.1, 0, 0],
      sword: [1.6, 0, 0.6, 0, -0.32, 0.1],
      armL: [0.45, 0, 0.12],
      foreL: [-0.5, 0, 0],
      flapF: [-0.3, 0, 0],
      flapB: [0.3, 0, 0],
    };
    timeline(b, t, [
      [0, {}],
      [0.3, hear, outExpo],
      [0.6, hear],
      [1.2, down, inOut],
      [1.35, { ...down, root: [0, 0, 0, 0, -0.48, -0.1] }, outQuad],
      [2.2, { ...down, chest: [0.3, 0, 0.04], head: [0.75, 0, 0.15] }, inOut],
    ], { head: 0.04, flapF: 0.06, flapB: 0.06 });
  }, (t) => thud(t, [[1.2, 0.05]]), 'De rodillas');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with her own blow and powers in place of the bash. */
export const ragnaClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), leap(), horn(), channel(), sally(), kneel()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { ragna: ragnaClips };
